import { timingSafeEqual } from 'node:crypto';
import { expand, extract } from '@noble/hashes/hkdf.js';
import { hmac } from '@noble/hashes/hmac.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { PakeClient } from './pake.js';

export interface PairingResult {
  authToken: string;
  clientId: string;
  pairingKey: Uint8Array;
}

const PAIRING_ERROR_MESSAGES: Record<number, string> = {
  400: 'Malformed pairing request',
  401: 'Wrong PIN — HMAC verification failed',
  403: 'Maximum paired clients reached or pairing attempts exhausted',
  404: 'Pairing session expired or unknown',
  410: 'Pairing PIN has expired — generate a new one from the device',
  429: 'Rate limit exceeded — wait before retrying',
};

const ANSI_CSI_PATTERN = new RegExp(`${String.fromCharCode(27)}\\[[0-?]*[ -/]*[@-~]`, 'g');
const MAX_SERVER_ERROR_LENGTH = 256;

function lengthPrefix(data: Uint8Array): Uint8Array {
  const result = new Uint8Array(4 + data.length);
  const view = new DataView(result.buffer, result.byteOffset, result.byteLength);
  view.setUint32(0, data.length, false);
  result.set(data, 4);
  return result;
}

function buildHmacTranscript(
  role: string,
  clientName: string,
  msgA: Uint8Array,
  msgB: Uint8Array,
): Uint8Array {
  const enc = new TextEncoder();
  const parts = [
    lengthPrefix(enc.encode('zaparoo-v1')),
    lengthPrefix(enc.encode('p256')),
    lengthPrefix(enc.encode(role)),
    lengthPrefix(enc.encode(clientName)),
    lengthPrefix(msgA),
    lengthPrefix(msgB),
  ];
  const total = parts.reduce((sum, p) => sum + p.length, 0);
  const buf = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    buf.set(part, offset);
    offset += part.length;
  }
  return buf;
}

// Retry fetch on 429 (rate limit). The server limits pairing endpoints
// to 1 req/sec per IP, and /pair/start consumes the token.
async function waitForRetry(signal?: AbortSignal): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, 1100);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(signal.reason ?? new Error('Pairing aborted'));
      },
      { once: true },
    );
  });
}

async function fetchWithRetry(url: string, init: RequestInit, maxRetries = 3): Promise<Response> {
  for (let attempt = 0; ; attempt++) {
    if (attempt > 0) await waitForRetry(init.signal ?? undefined);
    const response = await fetch(url, init);
    if (response.status !== 429 || attempt >= maxRetries) return response;
  }
}

async function pairingError(response: Response, fallback: string): Promise<Error> {
  let serverMessage: string | undefined;
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === 'string') serverMessage = sanitizeServerMessage(body.error);
  } catch {
    // Preserve status-based fallback when server did not return JSON.
  }
  return new Error(
    serverMessage || PAIRING_ERROR_MESSAGES[response.status] || `${fallback} (${response.status})`,
  );
}

function sanitizeServerMessage(value: string): string {
  const withoutAnsi = value.replace(ANSI_CSI_PATTERN, '');
  let result = '';
  for (const character of withoutAnsi) {
    const code = character.codePointAt(0) ?? 0;
    if (code < 0x20 || (code >= 0x7f && code <= 0x9f)) continue;
    result += character;
    if (result.length >= MAX_SERVER_ERROR_LENGTH) break;
  }
  return result.trim().slice(0, MAX_SERVER_ERROR_LENGTH);
}

function responseString(
  value: Record<string, unknown>,
  field: string,
  responseName: string,
): string {
  const result = value[field];
  if (typeof result !== 'string' || result.trim().length === 0) {
    throw new Error(`Pairing ${responseName} returned an invalid ${field}`);
  }
  return result;
}

function responseObject(value: unknown, responseName: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`Pairing ${responseName} returned an invalid response`);
  }
  return value as Record<string, unknown>;
}

async function pairingResponse(
  response: Response,
  responseName: string,
): Promise<Record<string, unknown>> {
  try {
    return responseObject(await response.json(), responseName);
  } catch (error) {
    if (error instanceof Error && error.message.startsWith(`Pairing ${responseName}`)) throw error;
    throw new Error(`Pairing ${responseName} returned invalid JSON`);
  }
}

export async function performPairing(
  host: string,
  port: number,
  pin: string,
  clientName = 'zaparoo-cli',
  timeoutMs = 30_000,
  protocol: 'http' | 'https' = 'http',
  pakeAlpha?: Uint8Array,
): Promise<PairingResult> {
  if (!/^\d{6}$/.test(pin)) throw new Error('Pairing PIN must be exactly 6 digits');
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(new Error(`Pairing timed out after ${timeoutMs}ms`)),
    timeoutMs,
  );
  try {
    return await performPairingWithSignal(
      host,
      port,
      pin,
      clientName,
      controller.signal,
      protocol,
      pakeAlpha,
    );
  } finally {
    clearTimeout(timeout);
  }
}

async function performPairingWithSignal(
  host: string,
  port: number,
  pin: string,
  clientName: string,
  signal: AbortSignal,
  protocol: 'http' | 'https',
  pakeAlpha?: Uint8Array,
): Promise<PairingResult> {
  const client = new PakeClient(pin, pakeAlpha);
  const msgA = client.bytes();
  const urlHost = host.includes(':') ? `[${host}]` : host;
  const origin = `${protocol}://${urlHost}:${port}`;

  // Step 1: POST /api/pair/start
  const startUrl = `${origin}/api/pair/start`;
  const startBody = JSON.stringify({
    pake: Buffer.from(msgA).toString('base64'),
    name: clientName,
  });

  const startResp = await fetchWithRetry(startUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: startBody,
    signal,
  });

  if (!startResp.ok) throw await pairingError(startResp, 'Pairing start failed');

  const startResult = await pairingResponse(startResp, 'start');
  const session = responseString(startResult, 'session', 'start');
  const pake = responseString(startResult, 'pake', 'start');
  const msgB = Buffer.from(pake, 'base64');

  // Step 2: Process server PAKE message and derive session key
  client.update(new Uint8Array(msgB));
  const sessionKey = client.sessionKey();

  // Step 3: Derive confirmation and pairing keys via HKDF
  // Salt is the concatenated PAKE transcript (msgA || msgB) for source-independent extraction
  const enc = new TextEncoder();
  const hkdfSalt = new Uint8Array(msgA.length + msgB.length);
  hkdfSalt.set(msgA, 0);
  hkdfSalt.set(new Uint8Array(msgB), msgA.length);
  const prk = extract(sha256, sessionKey, hkdfSalt);
  const confirmKeyA = expand(sha256, prk, enc.encode('zaparoo-confirm-A'), 32);
  const confirmKeyB = expand(sha256, prk, enc.encode('zaparoo-confirm-B'), 32);
  const pairingKey = expand(sha256, prk, enc.encode('zaparoo-pairing-v1'), 32);

  // Step 4: Compute client HMAC confirmation
  const transcript = buildHmacTranscript('client', clientName, msgA, new Uint8Array(msgB));
  const clientHmac = hmac(sha256, confirmKeyA, transcript);

  // Step 5: POST /api/pair/finish
  // The server rate-limits pairing endpoints at 1 req/sec per IP.
  // Since /pair/start just consumed the token, retry on 429.
  const finishUrl = `${origin}/api/pair/finish`;
  const finishBody = JSON.stringify({
    session,
    confirm: Buffer.from(clientHmac).toString('base64'),
  });

  const finishResp = await fetchWithRetry(finishUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: finishBody,
    signal,
  });

  if (!finishResp.ok) throw await pairingError(finishResp, 'Pairing finish failed');

  const finishResult = await pairingResponse(finishResp, 'finish');
  const authToken = responseString(finishResult, 'authToken', 'finish');
  const clientId = responseString(finishResult, 'clientId', 'finish');
  const confirm = responseString(finishResult, 'confirm', 'finish');

  // Step 6: Verify server HMAC
  const serverTranscript = buildHmacTranscript('server', clientName, msgA, new Uint8Array(msgB));
  const expectedServerHmac = hmac(sha256, confirmKeyB, serverTranscript);
  const serverHmac = Buffer.from(confirm, 'base64');

  if (
    serverHmac.length !== expectedServerHmac.length ||
    !timingSafeEqual(Buffer.from(expectedServerHmac), serverHmac)
  ) {
    throw new Error('Server HMAC verification failed — possible MITM attack');
  }

  return {
    authToken,
    clientId,
    pairingKey: new Uint8Array(pairingKey),
  };
}
